/**
 * FaceSheetDialog — the Face Sheet (WS-4 D5): the founder's "Emergency
 * Folder" one-pager. One button on the record opens this overlay; Print
 * hands the browser a folder-ready page (paper or save-as-PDF) for
 * hospital handoffs.
 *
 * The person as of today (LP-33): every section is
 * shown and an empty one says "None recorded". Name, date of birth and
 * age, pronouns, photo; primary language; home address and currently at;
 * contacts in emergency order; the care team with the care manager's
 * phone and email, the pharmacy and the preferred hospital; code status
 * and advance directives; allergies; conditions in force; functional
 * status; risk flags; insurance; referral source; transportation consent;
 * the month's appointments. Medications are not here: they are the Client
 * history's (QrfDialog.tsx), with every change over time.
 *
 * Data: the ONE ClientRecordSnapshot the page already fetched — the
 * sheet adds NO fetch (one audited read covers it; assembling the page
 * is the operator's deliberate handoff action). Derivations live in
 * src/lib/faceSheet.ts.
 *
 * Print mechanics live in printableSheet.tsx (PrintableSheetDialog): the
 * portal, the `face-sheet-open` print scope, Print and Download PDF.
 *
 * Register: this page travels to strangers in an ER — clarity over
 * brand. Black-on-paper text, borders instead of background washes
 * (browsers drop backgrounds in print), the safety facts (allergies,
 * code status) visually first. Acronym law holds (DNR/DNI/DNH caps,
 * "Full code"/"Comfort care" plain words); HCP expands to "Health care
 * proxy" for the stranger reading it.
 */

import { useState, type ReactNode } from 'react';
import { clientPhotoUrl } from '@/lib/clientsApi';
import { proseDate } from '@/lib/proseDate';
import { formatPhoneDisplay } from '@/lib/phone';
import { coverageCardFacts } from '@/lib/coverageCard';
import { nextMonthAppointments } from '@/lib/upcomingAppointments';
import {
  codeStatusSetLabel,
  appointmentWhen,
  recurrenceTagLabel,
  type AllergySeverity,
  type ClientEngagement,
  type ClientPlace,
  contactEffective,
  type ClientRecordSnapshot,
  type RiskFlagRow,
  type TransportationConsent,
} from '@/lib/clientRecord';
import { humanizeToken } from '@/lib/fieldLabels';
import { displayValue } from '@/lib/valueCase';
import {
  ageOn,
  contactRolesLine,
  faceSheetAllergies,
  faceSheetContacts,
  faceSheetCoverage,
  faceSheetConditions,
  faceSheetDirectives,
  faceSheetFunctional,
  faceSheetProviders,
  faceSheetRiskFlags,
  placeLine,
  providerAddress,
  providerEmail,
  providerPhone,
} from '@/lib/faceSheet';
import { riskCategoryLabel } from '@/lib/cmHome';
import { addressLine } from '@/lib/address';
import { appointmentPlaceLines } from '@/lib/appointmentPlace';
import { NONE, PrintableSheetDialog, SheetList, SheetSection } from './printableSheet';
import { NO_PRIMARY_TEXT, currentlyAt as currentlyAtRow, homeFact } from '@/lib/clientLocation';
import { FUNCTIONAL_LEVEL_PILL } from '@/lib/functionalLevel';

/** Mirrors VitalsBand's module-private ENGAGEMENT_LABELS (kept local so
 *  this build touches no concurrently-edited record files). */
const ENGAGEMENT_LABELS: Record<ClientEngagement, string> = {
  care_management: 'Care management',
  spice: 'SPICE',
  both: 'Care management + SPICE',
};

/** Mirrors the AllergiesCard severity wording (label text, sans pill). */
const SEVERITY_LABELS: Record<AllergySeverity, string> = {
  severe: 'Severe',
  moderate: 'Moderate',
  mild: 'Mild',
  unknown: 'Severity unknown',
};

/** The strip's consent words. */
const CONSENT_WORDS: Record<TransportationConsent, string> = {
  consented: 'Consented',
  declined: 'Declined',
};

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

const RISK_LEVEL_WORD: Record<RiskFlagRow['level'], string> = {
  elevated: 'Elevated',
  watch: 'Watch',
  resolved: 'Resolved',
};

/** The quiet empty words inline after a label. */
const NoneInline = (): ReactNode => <span className="italic text-ink-soft">None recorded</span>;

/** A labelled place line in the care team: "Pharmacy: name · phone". */
function PlaceLine({ label, place, testId }: { label: string; place: ClientPlace | null; testId: string }): ReactNode {
  const line = placeLine(place);
  return (
    <p className="text-[0.78125rem]" data-testid={testId}>
      <span className="font-semibold">{label}:</span> {line ?? <NoneInline />}
      {place?.address && <span className="block text-[0.71875rem] text-ink-soft">{place.address}</span>}
    </p>
  );
}

export function FaceSheetDialog({
  record,
  today,
  onClose,
  tenantSlug,
  photoVersion,
}: {
  record: ClientRecordSnapshot;
  /** The page-boundary day (TodayContext discipline — the sheet never
   *  reads the clock; the prepared-on line and age derive from this). */
  today: string;
  onClose: () => void;
  /** 0142 (the humanity upgrade): the tenant slug for the photo serve URL.
   *  Optional — when absent (direct-render tests) the sheet renders the
   *  initials monogram, unchanged. */
  tenantSlug?: string;
  /** Cache-bust token forwarded from the record hero after a photo change. */
  photoVersion?: number;
}): ReactNode {
  const [photoErrored, setPhotoErrored] = useState(false);

  const { vitals } = record;
  // Home reads homeFact, as the Contact band and the Addresses card do.
  const home = homeFact(record.addresses);
  const currentlyAt = currentlyAtRow(record.addresses);
  const contacts = faceSheetContacts(record.contacts);
  const providers = faceSheetProviders(record.providers);
  const coverage = faceSheetCoverage(record.coverage);
  // The client as of today: what is in force, from the same snapshot (no
  // new read). What changed over time is the Client history's.
  const riskFlags = faceSheetRiskFlags(record.riskFlags);
  const conditions = faceSheetConditions(record.conditions);
  const functional = faceSheetFunctional(record.functionalStatus);
  const allergies = faceSheetAllergies(record);
  // G1 (Soak #2 #23): the month's upcoming appointments (shared helper —
  // stale-past-scheduled rows folded out), soonest first.
  const upcoming = nextMonthAppointments(record.appointments, today);

  // The CriticalStrip's code-status semantics: null OR all-'unknown'
  // reads "Not recorded" (the honest state; never shouted).
  const codeStatus = vitals.codeStatus;
  const codeStatusRecorded = codeStatus !== null && codeStatus.values.some((v) => v !== 'unknown');
  const directives = faceSheetDirectives(record);

  // D3 (Soak #2 #21): the DOB line and the Prepared date share ONE line,
  // joined with a semicolon (the founder's ask) — no floating top-right
  // "Prepared" element anymore.
  const demographicsLine = `${[
    vitals.dob ? `DOB ${proseDate(vitals.dob)} (age ${ageOn(vitals.dob, today)})` : 'DOB not recorded',
    vitals.gender ? humanizeToken(vitals.gender) : null,
    vitals.pronouns,
  ]
    .filter(Boolean)
    .join(' · ')}; Prepared ${proseDate(today)}`;

  // Soak #2 #20: the client reach line renders the phone formatted.
  const reachLine = [vitals.phone ? formatPhoneDisplay(vitals.phone) : null, vitals.email]
    .filter(Boolean)
    .join(' · ');

  const careManagerReach = [
    vitals.owner?.phone ? formatPhoneDisplay(vitals.owner.phone) : null,
    vitals.owner?.email ?? null,
  ].filter((s): s is string => s !== null && s !== '');

  return (
    <PrintableSheetDialog
      title="Face Sheet"
      blurb={
        <>
          A one-page summary for hospital handoffs. Download it as a PDF for the client&rsquo;s Emergency
          Folder, or print it.
        </>
      }
      testIdPrefix="face-sheet"
      printElementId="face-sheet-print"
      pdfStem="FaceSheet"
      clientName={vitals.displayName}
      onClose={onClose}
    >
        {/* ============== THE SHEET (the part that prints) ============== */}
        <div id="face-sheet-print" data-testid="face-sheet" className="text-ink">
          {/* -------- Identity header -------- */}
          <header className="flex items-start gap-4 border-b-2 border-sage-800 pb-3">
            {/* 0142 (the humanity upgrade): the identity avatar carries the
                client's face when one is on file (a warmer, more human sheet
                for the stranger in the ER); the bordered initials monogram is
                the fallback (no photo / no slug / load error). */}
            {vitals.hasPhoto && tenantSlug && !photoErrored ? (
              <img
                src={clientPhotoUrl(tenantSlug, record.clientId, photoVersion || undefined)}
                alt={`Photo of ${vitals.displayName}`}
                data-testid="face-sheet-photo-img"
                className="h-14 w-14 shrink-0 rounded-full border-2 border-sage-800 object-cover"
                onError={() => setPhotoErrored(true)}
              />
            ) : (
              <span
                aria-hidden="true"
                data-testid="face-sheet-photo-initials"
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-sage-800 font-display text-xl text-sage-800"
              >
                {initialsOf(vitals.displayName) || '?'}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-soft">
                Face Sheet · Client
              </p>
              <h2 className="font-display text-[26px] leading-tight" data-testid="face-sheet-name">
                {vitals.displayName}
                {/* The name the client goes by, beside the legal name on
                    the face sheet too. No size or colour class of its
                    own, so it inherits the h2's 26px display and the
                    sheet's own ink. Guard +
                    render on the TRIMMED value so a whitespace-only value
                    never renders an empty "( )". */}
                {vitals.preferredName?.trim() && (
                  <>
                    {' '}
                    <span data-testid="face-sheet-preferred-name">{`(${vitals.preferredName.trim()})`}</span>
                  </>
                )}
              </h2>
              <p className="mt-0.5 text-[13px]" data-testid="face-sheet-demographics">
                {demographicsLine}
              </p>
              {reachLine && <p className="text-[13px]">{reachLine}</p>}
              {/* LP-33: the primary language, always a line, so the stranger
                  reading this knows how to speak to the person. */}
              <p className="text-[13px]" data-testid="face-sheet-language">
                <span className="font-semibold">Primary language:</span>{' '}
                {vitals.primaryLanguage ?? <NoneInline />}
              </p>
            </div>
          </header>

          {/* -------- Safety band: the facts an ER needs first -------- */}
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="face-sheet-safety">
            <section
              data-testid="face-sheet-allergies"
              className="rounded-card border-2 border-pepper-deep p-3"
            >
              <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-pepper-deep">
                Allergies
              </h3>
              {allergies.kind === 'recorded' ? (
                <ul className="mt-1 flex flex-col gap-0.5 text-[13.5px] font-semibold">
                  {allergies.rows.map((a) => (
                    <li key={a.id}>
                      {displayValue(a.substance, 'allergy')}
                      {(a.reaction || a.severity) && (
                        <span className="font-normal text-ink-soft">
                          {': '}
                          {displayValue(
                            [a.reaction, a.severity ? SEVERITY_LABELS[a.severity] : null]
                              .filter(Boolean)
                              .join(', '),
                          )}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : allergies.kind === 'none_known' ? (
                <p className="mt-1 text-[13.5px]">
                  No known allergies, confirmed {proseDate(allergies.confirmedOn)}.
                </p>
              ) : (
                <p className="mt-1 text-[13.5px] italic">
                  No allergies recorded; not confirmed allergy-free.
                </p>
              )}
            </section>
            <section
              data-testid="face-sheet-code-status"
              className="rounded-card border-2 border-sage-800 p-3"
            >
              <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
                Code status
              </h3>
              <p className="mt-1 text-[17px] font-semibold">
                {codeStatusRecorded ? (
                  codeStatusSetLabel(codeStatus.values)
                ) : (
                  <span className="text-[13.5px] font-normal italic text-ink-soft">
                    Not recorded
                  </span>
                )}
              </p>
              {/* LP-33: the advance directives always have their line, so
                  "no POLST on file" is said, not left to be inferred. */}
              <p className="mt-0.5 text-[11.5px] text-ink-soft" data-testid="face-sheet-instruments">
                <span className="font-semibold">Advance directives:</span>{' '}
                {directives.length > 0 ? directives.join(' · ') : <NoneInline />}
              </p>
            </section>
            <section
              data-testid="face-sheet-risk-flags"
              className="rounded-card border border-sage-300 p-3 sm:col-span-2"
            >
              <h3 className="text-[0.65625rem] font-semibold uppercase tracking-[0.14em] text-ink-soft">
                Risk flags
              </h3>
              {riskFlags.length === 0 ? (
                <p className="mt-1 text-[0.78125rem] italic text-ink-soft">None recorded</p>
              ) : (
                <ul className="mt-1 flex flex-col gap-0.5 text-[0.84375rem]">
                  {riskFlags.map((f) => (
                    <li key={f.id}>
                      <span className="font-semibold">{riskCategoryLabel(f.category)}</span>
                      {' · '}
                      {RISK_LEVEL_WORD[f.level]}
                      {f.basis && <span className="text-ink-soft">{`: ${displayValue(f.basis)}`}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* -------- Addresses -------- */}
          <SheetSection title="Addresses" testId="face-sheet-addresses" className="mt-3">
            <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-[12.5px] sm:grid-cols-2">
              <p>
                <span className="font-semibold">Home:</span>{' '}
                {home.kind === 'home' ? (
                  addressLine(home.row)
                ) : (
                  <span className="italic text-ink-soft">
                    {home.kind === 'no_primary' ? NO_PRIMARY_TEXT : 'None recorded'}
                  </span>
                )}
              </p>
              {/* LP-33: Currently at always has its line: another place when
                  the client is away, "At home" when the home is where they
                  are, None recorded when nothing says. */}
              <p data-testid="face-sheet-currently-at">
                <span className="font-semibold">Currently at:</span>{' '}
                {currentlyAt !== null ? (
                  addressLine(currentlyAt)
                ) : home.kind === 'home' ? (
                  'At home'
                ) : (
                  <NoneInline />
                )}
              </p>
            </div>
          </SheetSection>

          {/* -------- Contacts (emergency first) -------- */}
          <SheetSection title="Contacts" testId="face-sheet-contacts" className="mt-3">
            {contacts.length === 0 ? (
              NONE
            ) : (
              <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
                {contacts.map((c) => {
                  const roles = contactRolesLine(c, record.vitals.pronouns);
                  const shown = contactEffective(c);
                  return (
                    <li key={c.id} className="text-[12.5px]">
                      <span className="font-semibold">{c.name}</span>
                      {c.relationship && <span className="text-ink-soft"> · {c.relationship}</span>}
                      {roles && <span className="block text-[11.5px] text-ink-soft">{roles}</span>}
                      {(shown.phone || shown.email) && (
                        <span className="block">
                          {[shown.phone ? formatPhoneDisplay(shown.phone) : null, shown.email]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      )}
                      {shown.address && <span className="block text-ink-soft">{shown.address}</span>}
                    </li>
                  );
                })}
              </ul>
            )}
          </SheetSection>

          {/* -------- Care team -------- */}
          <SheetSection title="Care team" testId="face-sheet-care-team" className="mt-3">
            <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              <p className="text-[12.5px]" data-testid="face-sheet-cm">
                <span className="font-semibold">
                  {vitals.owner?.displayName ?? <span className="italic font-normal text-ink-soft">Unassigned</span>}
                </span>
                <span className="text-ink-soft"> · Care manager</span>
                <span className="block text-[11.5px] text-ink-soft">
                  {ENGAGEMENT_LABELS[vitals.engagement]}
                </span>
                {/* LP-33: the care manager's phone and email, the number a
                    hospital calls. */}
                {careManagerReach.length > 0 && <span className="block">{careManagerReach.join(' · ')}</span>}
              </p>
              {vitals.spiceTherapist && (
                <p className="text-[12.5px]" data-testid="face-sheet-spice-therapist">
                  <span className="font-semibold">{vitals.spiceTherapist.displayName}</span>
                  <span className="text-ink-soft"> · SPICE therapist</span>
                </p>
              )}
              {providers.map((p) => {
                const phone = providerPhone(p);
                const address = providerAddress(p);
                const email = providerEmail(p);
                return (
                  <p key={p.id} className="text-[12.5px]">
                    <span className="font-semibold">{p.displayName}</span>
                    {(p.role || p.specialty) && (
                      <span className="text-ink-soft">
                        {' '}
                        · {[p.role, p.specialty].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {p.organization && (
                      <span className="block text-[11.5px] text-ink-soft">{p.organization}</span>
                    )}
                    {(phone || address || email) && (
                      <span className="block">{[phone, email, address].filter(Boolean).join(' · ')}</span>
                    )}
                  </p>
                );
              })}
              {/* LP-33: the pharmacy and the preferred hospital, each with
                  its phone when the Directory knows one; always a line. */}
              <PlaceLine label="Pharmacy" place={vitals.pharmacy} testId="face-sheet-pharmacy" />
              <PlaceLine label="Preferred hospital" place={vitals.preferredHospital} testId="face-sheet-preferred-hospital" />
            </div>
          </SheetSection>

          {/* -------- Conditions in force -------- */}
          <SheetSection title="Conditions" testId="face-sheet-conditions" className="mt-3">
            <SheetList
              empty="None recorded"
              items={conditions.map((c) => ({
                key: c.id,
                body: (
                  <>
                    <span className="font-semibold">{displayValue(c.name, 'diagnosis')}</span>
                    {c.status === 'watch' && <span className="text-ink-soft"> (watch)</span>}
                    {c.notedOn && <span className="text-ink-soft">{` · noted ${proseDate(c.notedOn)}`}</span>}
                  </>
                ),
              }))}
            />
          </SheetSection>

          {/* -------- Functional status, assessed activities -------- */}
          <SheetSection title="Functional status" testId="face-sheet-functional-status" className="mt-3">
            <SheetList
              empty="None recorded"
              items={functional.map((f) => ({
                key: f.id,
                body: (
                  <>
                    <span className="font-semibold">{humanizeToken(f.activity)}</span>
                    {` · ${FUNCTIONAL_LEVEL_PILL[f.level].label}`}
                    {f.note && <span className="text-ink-soft">{`: ${displayValue(f.note)}`}</span>}
                  </>
                ),
              }))}
            />
          </SheetSection>

          {/* -------- Insurance -------- */}
          <SheetSection title="Insurance" testId="face-sheet-insurance" className="mt-3">
            {coverage.length === 0 ? (
              NONE
            ) : (
              <ul className="flex flex-wrap gap-x-6 gap-y-1 text-[12.5px]">
                {coverage.map((c) => (
                  <li key={c.id}>
                    {/* Founder's order (Soak #2 Care Record #9): Program /
                        Provider / Status (ALWAYS, not only when inactive) /
                        Verified (ALWAYS when present, previously never shown) /
                        Details. */}
                    <span className="font-semibold">{humanizeToken(c.program)}</span>
                    {c.provider && <span> · {c.provider}</span>}
                    <span className="text-ink-soft"> · {humanizeToken(c.status)}</span>
                    {c.verifiedOn && (
                      <span className="text-ink-soft"> · Verified {proseDate(c.verifiedOn)}</span>
                    )}
                    {/* The card's fields that hold a value, in the form's
                        order and words (the Insurance card's own list). */}
                    {coverageCardFacts(c).length > 0 && (
                      <span className="block text-[0.75rem]" data-testid="face-sheet-insurance-card">
                        {coverageCardFacts(c)
                          .map((f) => `${f.label} ${f.value}`)
                          .join(' · ')}
                      </span>
                    )}
                    {c.detail && <span className="block text-[11.5px] text-ink-soft">{c.detail}</span>}
                  </li>
                ))}
              </ul>
            )}
          </SheetSection>

          {/* -------- Referral source (LP-33) -------- */}
          <SheetSection title="Referral source" testId="face-sheet-referral" className="mt-3">
            {vitals.referredBy ? (
              <p className="text-[0.78125rem]">
                {vitals.referredBy.label}
                {vitals.referredBy.source === 'intake' && <span className="text-ink-soft"> (from the intake)</span>}
              </p>
            ) : (
              NONE
            )}
          </SheetSection>

          {/* -------- Consent (LP-33): whether staff may drive the client. -------- */}
          <SheetSection title="Consent" testId="face-sheet-consent" className="mt-3">
            <p className="text-[0.78125rem]" data-testid="face-sheet-transportation-consent">
              <span className="font-semibold">Transportation consent:</span>{' '}
              {vitals.transportationConsent ? CONSENT_WORDS[vitals.transportationConsent.value] : <NoneInline />}
            </p>
          </SheetSection>

          {/* -------- Upcoming appointments (Soak #2 #23) -------- */}
          <SheetSection
            title="Upcoming appointments"
            testId="face-sheet-upcoming-appointments"
            className="mt-3"
          >
            {upcoming.length > 0 ? (
              <ul className="flex flex-col gap-1 text-[12.5px]">
                {upcoming.map((a) => {
                  // The provider display can be an empty string on a
                  // payload-thin row — join only the parts that exist so
                  // the line never leads with a bare separator.
                  // G2 (Soak #2 Wave B): the NEXT occurrence (never a stale
                  // recurring anchor) + the cadence tag; the place below.
                  const when = appointmentWhen(a, today) ?? (a.dateText ? displayValue(a.dateText) : a.dateText);
                  const cadence = recurrenceTagLabel(a.recurrence);
                  const place = appointmentPlaceLines(a);
                  return (
                    <li key={a.id}>
                      {a.provider && <span className="font-semibold">{a.provider}</span>}
                      {a.provider && when ? ' · ' : ''}
                      {when}
                      {a.purpose && <span className="text-ink-soft"> · {displayValue(a.purpose, 'appointment_purpose')}</span>}
                      {cadence !== null && <span className="text-ink-soft"> · {cadence}</span>}
                      {/* 0233: where, on labelled lines of its own. */}
                      {place.name !== null && <span className="block text-ink-soft">Location: {place.name}</span>}
                      {place.address !== null && <span className="block text-ink-soft">Address: {place.address}</span>}
                      {place.text !== null && <span className="block text-ink-soft">Location: {place.text}</span>}
                      {a.outcome !== undefined && a.outcome.trim() !== '' && (
                        <span data-testid="face-sheet-appointment-note" className="block whitespace-pre-wrap text-ink-soft">
                          Notes: {a.outcome}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-[12.5px] italic text-ink-soft">No upcoming appointments</p>
            )}
          </SheetSection>

          <p className="mt-4 border-t border-sage-300 pt-2 text-[10.5px] text-ink-soft">
            Prepared {proseDate(today)} from the client record. Details may have changed since;
            when in doubt, call the care manager.
          </p>
        </div>
    </PrintableSheetDialog>
  );
}
